import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, Award, CheckCircle2, AlertCircle, Search, Download, ArrowLeft, Loader2 } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BASE_URL || process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function VerifyCertificate() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const codeParam = queryParams.get('code') || '';

  const [inputCode, setInputCode] = useState(codeParam);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const performVerification = async (codeToVerify) => {
    const code = (codeToVerify || inputCode || '').trim();
    if (!code) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await axios.get(`${API_BASE_URL}/api/students/peermate/verify-credential/${encodeURIComponent(code)}`);
      if (res.data?.success && res.data?.data) {
        setResult(res.data.data);
      } else {
        setError(res.data?.message || 'Certificate record could not be verified.');
      }
    } catch (err) {
      console.error('Verification query failed:', err);
      setError(err.response?.data?.message || 'No official completion credential matches this code.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (codeParam) {
      performVerification(codeParam);
    }
  }, [codeParam]);

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8 text-slate-800">
      <div className="max-w-2xl mx-auto space-y-6">
        
        {/* Back navigation */}
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-blue-600 transition">
          <ArrowLeft size={14} />
          <span>Return to Peer2Learn</span>
        </Link>

        {/* Header Banner */}
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-sm text-center space-y-3">
          <div className="w-14 h-14 rounded-2xl bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-600 mx-auto shadow-sm">
            <ShieldCheck size={28} />
          </div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight">Official Certificate Verification</h1>
          <p className="text-xs text-slate-500 max-w-md mx-auto">
            Verify academic completion credentials issued by Peer2Learn and PeerMate AI.
          </p>

          {/* Search Box */}
          <form 
            onSubmit={(e) => { e.preventDefault(); performVerification(); }}
            className="flex items-center gap-2 max-w-md mx-auto pt-2"
          >
            <div className="relative flex-1">
              <input
                type="text"
                value={inputCode}
                onChange={(e) => setInputCode(e.target.value)}
                placeholder="e.g. P2L-CERT-1-4A414D-2026"
                className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-300 text-xs font-mono font-semibold text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-600 shadow-inner"
              />
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3 pointer-events-none" />
            </div>
            <button
              type="submit"
              disabled={loading || !inputCode.trim()}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl text-xs font-bold transition shadow-sm"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : 'Verify'}
            </button>
          </form>
        </div>

        {/* Verification Result Card */}
        {result && (
          <div className="bg-white rounded-3xl p-6 sm:p-8 border-2 border-emerald-500 shadow-lg space-y-6 animate-fade-in">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <div className="flex items-center gap-2 text-emerald-700 font-extrabold text-sm">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                <span>OFFICIAL VERIFIED CREDENTIAL</span>
              </div>
              <span className="text-[11px] font-mono font-bold px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                {result.certificateCode}
              </span>
            </div>

            <div className="space-y-4">
              <div>
                <span className="text-[11px] uppercase tracking-wider font-bold text-slate-400 block">Recipient</span>
                <p className="text-xl font-black text-slate-900">{result.recipientName}</p>
                {result.recipientEmail && (
                  <p className="text-xs text-slate-500">{result.recipientEmail}</p>
                )}
              </div>

              <div>
                <span className="text-[11px] uppercase tracking-wider font-bold text-slate-400 block">Curriculum Mastered</span>
                <p className="text-lg font-bold text-blue-600">{result.courseName}</p>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-2 border-t border-slate-100 text-xs">
                <div>
                  <span className="text-slate-400 block text-[10px] font-bold uppercase">Issued By</span>
                  <p className="font-semibold text-slate-700">{result.issuedBy}</p>
                </div>
                <div>
                  <span className="text-slate-400 block text-[10px] font-bold uppercase">Date of Issuance</span>
                  <p className="font-semibold text-slate-700">
                    {result.verifiedAt ? new Date(result.verifiedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Verified'}
                  </p>
                </div>
              </div>
            </div>

            {/* Direct PDF Download Action */}
            <div className="pt-2">
              <a
                href={`${API_BASE_URL}/api/students/peermate/certificate-pdf/${encodeURIComponent(result.certificateCode.split('-')[2] || '1')}/${encodeURIComponent(result.courseName)}`}
                target="_blank"
                rel="noreferrer"
                className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs flex items-center justify-center gap-2 transition shadow-md shadow-emerald-600/20"
              >
                <Download size={15} />
                <span>Download Verified PDF Certificate</span>
              </a>
            </div>
          </div>
        )}

        {/* Error Notice */}
        {error && (
          <div className="bg-white rounded-3xl p-6 border border-rose-200 shadow-sm flex items-start gap-3 text-xs text-rose-700">
            <AlertCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="font-bold">Credential Verification Notice</p>
              <p className="text-slate-600">{error}</p>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
